/**
 * Delivery-side resizing for stored blog images.
 *
 * Every upload is normalized to one WebP at up to 2560px on the long edge (the
 * API's `lib/image-compression.ts`), because the article lightbox opens images
 * at close to full size and that is the copy it needs. Every other surface shows
 * them far smaller: the homepage preview slot is 576 CSS px wide, a blog index
 * card 368, a gallery thumbnail as little as 280. Handing those the 2560px file
 * spends megabytes on pixels the browser immediately discards.
 *
 * Supabase Storage can resize on the way out, so nothing has to change about
 * what we store: the same object served through the render endpoint comes back
 * at whatever width was asked for, with the stored file left alone for the
 * lightbox. Variants inherit the one-year `Cache-Control` the upload set and are
 * cached at the edge per URL, so a width is transformed once and then served
 * from cache.
 */

/** What a public object URL looks like, and what the resizing one looks like. */
const OBJECT_SEGMENT = '/storage/v1/object/public/';
const RENDER_SEGMENT = '/storage/v1/render/image/public/';

/**
 * WebP quality for the resized copy. The stored file is already q82; re-encoding
 * a downscaled copy at 75 is visually indistinguishable at these display sizes
 * and meaningfully smaller, and downscaling hides encoder noise besides.
 */
const QUALITY = 75;

/**
 * `resize` is not optional in practice. The endpoint defaults to `cover`, and
 * with only a `width` given the height defaults to the *original* height, so a
 * portrait 1920x2560 cover comes back center-cropped to 1152x2560 rather than
 * scaled to 1152x1536 -- taller, heavier and differently framed than the source.
 * `contain` with a single width is the proportional resize we actually want.
 */
const RESIZE = 'contain';

/**
 * `url` rewritten to serve at `width` device pixels, or `undefined` when it is
 * not a resizable stored object.
 *
 * Anything we did not store is returned as `undefined` rather than guessed at:
 * an external cover URL has no render endpoint behind it, and an SVG has no
 * pixels to resample. Callers keep the original as `src` and treat a defined
 * result as an optional upgrade, so an unrecognized URL just renders as it does
 * today.
 */
export function blogImageUrl(url: string, width: number): string | undefined {
  if (!url.includes(OBJECT_SEGMENT)) return undefined;
  // The transformer rasterizes, which would throw away the one property that
  // makes a vector worth using.
  if (/\.svg($|\?)/i.test(url)) return undefined;
  // A URL that already carries a query is one we did not build; leaving it alone
  // beats appending a second, conflicting set of transform parameters.
  if (url.includes('?')) return undefined;

  return `${url.replace(OBJECT_SEGMENT, RENDER_SEGMENT)}?width=${Math.round(width)}&resize=${RESIZE}&quality=${QUALITY}`;
}

/**
 * A `srcset` for `url` at each of `widths`, or `undefined` when `url` is not
 * resizable.
 *
 * Widths are device pixels, so a caller lists what the layout needs at 1x and
 * 2x and pairs this with a `sizes` describing the CSS slot. Asking for more than
 * the stored 2560px is harmless: the endpoint clamps to the original rather than
 * upscaling, so the largest candidate is at worst a duplicate of it.
 */
export function blogImageSrcSet(url: string, widths: number[]): string | undefined {
  const candidates = widths
    .map((w) => {
      const resized = blogImageUrl(url, w);
      return resized ? `${resized} ${Math.round(w)}w` : undefined;
    })
    .filter((c): c is string => c !== undefined);

  return candidates.length > 0 ? candidates.join(', ') : undefined;
}
